import { Download, EllipsisVertical, Share, Smartphone, X } from "lucide-react"
import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { isIos, isTouchDevice } from "@/lib/device"
import { useTranslation } from "@/lib/i18n"
import {
  type BeforeInstallPromptEvent,
  clearStashedInstallPrompt,
  getStashedInstallPrompt,
  PWA_INSTALL_AVAILABLE_EVENT,
} from "@/lib/pwa-install"
import { isStandalone } from "@/lib/standalone"

// Records why the banner is hidden. A dismissal stores a timestamp and re-shows
// after the snooze window; installing (or accepting the native prompt) stores the
// permanent sentinel and never re-shows on this device.
const STORAGE_KEY = "pwa-install-dismissed"
const SNOOZE_DAYS = 14
const SNOOZE_MS = SNOOZE_DAYS * 24 * 60 * 60 * 1000
const PERMANENT = "installed"

// How long we wait for Chrome's `beforeinstallprompt` before deciding this
// browser will never fire one (Firefox / some vendor browsers on Android) and
// falling back to manual "open the menu" instructions.
const MANUAL_FALLBACK_DELAY_MS = 3000

// True when a past dismissal or install still suppresses the banner. A dismissal
// stores `Date.now()` and expires after SNOOZE_MS; an install stores PERMANENT
// and never expires. The legacy "1" flag (older builds, which nagged forever)
// parses as an ancient timestamp, so it reads as an expired snooze and the banner
// returns once — the graceful migration for existing testers.
export function isSuppressed(raw: string | null, now: number): boolean {
  if (!raw) {
    return false
  }
  if (raw === PERMANENT) {
    return true
  }
  const dismissedAt = Number(raw)
  return Number.isFinite(dismissedAt) && now - dismissedAt < SNOOZE_MS
}

type Mode = "hidden" | "install" | "ios" | "manual"

export function PwaInstallPrompt() {
  const { t } = useTranslation()
  const [mode, setMode] = useState<Mode>("hidden")
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null)

  useEffect(() => {
    if (isStandalone() || isSuppressed(localStorage.getItem(STORAGE_KEY), Date.now())) {
      return
    }

    function showInstall(event: BeforeInstallPromptEvent) {
      setInstallEvent(event)
      setMode("install")
    }

    // The native event may have fired before this component mounted — captured at
    // module scope in the entrypoint. Consume the stash first, then also listen
    // for the re-dispatched event in case it lands after mount.
    const stashed = getStashedInstallPrompt()
    if (stashed) {
      showInstall(stashed)
    }

    function onInstallAvailable() {
      const event = getStashedInstallPrompt()
      if (event) {
        showInstall(event)
      }
    }

    function onInstalled() {
      localStorage.setItem(STORAGE_KEY, PERMANENT)
      setMode("hidden")
    }

    window.addEventListener(PWA_INSTALL_AVAILABLE_EVENT, onInstallAvailable)
    window.addEventListener("appinstalled", onInstalled)

    if (isIos()) {
      setMode("ios")
    }

    // Touch devices that are not iOS and never get a `beforeinstallprompt` fall
    // back to manual instructions after a short grace period. `install` and `ios`
    // modes already win, so only promote from `hidden`.
    let fallbackTimer: number | undefined
    if (!isIos() && isTouchDevice() && !stashed) {
      fallbackTimer = window.setTimeout(() => {
        setMode((current) => (current === "hidden" ? "manual" : current))
      }, MANUAL_FALLBACK_DELAY_MS)
    }

    return () => {
      window.removeEventListener(PWA_INSTALL_AVAILABLE_EVENT, onInstallAvailable)
      window.removeEventListener("appinstalled", onInstalled)
      if (fallbackTimer !== undefined) {
        window.clearTimeout(fallbackTimer)
      }
    }
  }, [])

  function snooze() {
    localStorage.setItem(STORAGE_KEY, String(Date.now()))
    setMode("hidden")
  }

  async function install() {
    if (!installEvent) {
      return
    }
    await installEvent.prompt()
    const { outcome } = await installEvent.userChoice
    clearStashedInstallPrompt()
    // Accepting installs the app for good; cancelling just snoozes the nag.
    if (outcome === "accepted") {
      localStorage.setItem(STORAGE_KEY, PERMANENT)
    } else {
      localStorage.setItem(STORAGE_KEY, String(Date.now()))
    }
    setMode("hidden")
  }

  if (mode === "hidden") {
    return null
  }

  return (
    <section
      aria-label={t("pwa_install.aria_label")}
      className="fixed inset-x-0 bottom-0 z-50 flex justify-center p-3 print:hidden"
    >
      <div className="flex w-full max-w-[400px] items-start gap-3 rounded-2xl border border-border bg-card p-4 shadow-2xl">
        <span className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-pine-100 text-pine-800">
          <Smartphone className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-display font-semibold text-sm">{t("pwa_install.title")}</p>
          {mode === "install" && (
            <>
              <p className="mt-0.5 text-muted-foreground text-sm">{t("pwa_install.description")}</p>
              <Button type="button" className="mt-3 w-full" onClick={install}>
                <Download className="h-4 w-4" />
                {t("pwa_install.install")}
              </Button>
            </>
          )}
          {mode === "ios" && (
            <p className="mt-0.5 flex items-start gap-1.5 text-muted-foreground text-sm">
              <Share className="mt-0.5 h-4 w-4 flex-none" aria-hidden="true" />
              <span>{t("pwa_install.ios_hint")}</span>
            </p>
          )}
          {mode === "manual" && (
            <p className="mt-0.5 flex items-start gap-1.5 text-muted-foreground text-sm">
              <EllipsisVertical className="mt-0.5 h-4 w-4 flex-none" aria-hidden="true" />
              <span>{t("pwa_install.manual_hint")}</span>
            </p>
          )}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={t("pwa_install.dismiss")}
          className="-mr-1 -mt-1 flex-none"
          onClick={snooze}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </section>
  )
}
